import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import './index.css'
import './assets/scss/material-kit-react.css'
import indexRoutes from './routes/index.jsx'
import { CompatRoute } from './compat/router'

import { ThemeProvider, createTheme } from '@mui/material/styles'
import { ThemeProvider as LegacyThemeProvider } from '@mui/styles'
import { ThemeProvider as SCThemeProvider } from 'styled-components'
import { colors as scColors, styles as scStyles } from '@get-wrecked/simple-components'
import Footer from './presentations/Footer'

import {
  getReportingPeriod,
  setReportingPeriod,
  getPeriodCount,
  setPeriodCount,
  getEndDate,
  setEndDate,
  invalidateGameData,
  retreiveReportingPeriodFromQueryString,
  retrievePeriodCountFromQueryString,
  retrieveEndDateFromQueryString,
  sanitizePeriodCount,
  sanitizeEndDate,
  getReportingPeriodQueryString,
  allowCustomDateRange
} from './data/App'

import AuthProvider from './context/providers/AuthProvider'
import TopNavigation from './presentations/Navigation/TopNavigation'
import ModalProvider from './context/providers/ModalProvider'
import ModalContainer from './presentations/ModalContainer'
import Page from './presentations/Page'
import CategoryProvider from './context/providers/CategoryProvider'
import BookmarkProvider from './context/providers/BookmarkProvider'

const theme = createTheme({
  palette: {
    mode: 'dark'
  },
  components: {
    MuiTextField: {
      defaultProps: { variant: 'standard' }
    },
    MuiFormControl: {
      defaultProps: { variant: 'standard' }
    },
    MuiSelect: {
      defaultProps: { variant: 'standard' }
    },
    MuiInputLabel: {
      defaultProps: { variant: 'standard' }
    }
  }
})

const App = () => {
  return (
    <AuthProvider>
      <CategoryProvider>
        <ModalProvider>
          <BookmarkProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </BookmarkProvider>
        </ModalProvider>
      </CategoryProvider>
    </AuthProvider>
  )
}

function AppContent() {
  const location = useLocation()
  const navigate = useNavigate()

  const history = React.useMemo(
    () => ({
      push: (path, state) => navigate(path, { state }),
      replace: (path, state) => navigate(path, { replace: true, state }),
      go: (n) => navigate(n),
      goBack: () => navigate(-1)
    }),
    [navigate]
  )

  const match = { params: {}, url: location.pathname, path: location.pathname }

  const isEmbed = location.pathname.startsWith('/embed')

  if (isEmbed) {
    return <EmbedApp history={history} location={location} match={match} />
  }
  return <MainApp history={history} location={location} match={match} />
}

function RouteContent() {
  return (
    <Routes>
      {indexRoutes.map((prop, key) => {
        return <Route path={prop.path} key={key} element={<CompatRoute component={prop.component} />} />
      })}
    </Routes>
  )
}

class MainApp extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      reportingPeriod: getReportingPeriod(),
      periodCount: getPeriodCount(),
      endDate: getEndDate()
    }
  }

  scopeChange = (reportingPeriod, periodCount, endDate) => {
    if (reportingPeriod === getReportingPeriod() && periodCount === getPeriodCount() && endDate === getEndDate()) {
      return
    }

    invalidateGameData(getReportingPeriod())
    setReportingPeriod(reportingPeriod)
    setPeriodCount(getReportingPeriod(), periodCount)
    setEndDate(endDate)

    this.setState({ reportingPeriod, periodCount, endDate }, () => {
      this.props.history.push(`${window.location.pathname}?${getReportingPeriodQueryString()}`, this.state)
    })
  }

  periodChange = (value) => {
    if (value === getReportingPeriod()) {
      return
    }

    setReportingPeriod(value)
    this.props.history.replace(`${window.location.pathname}?${getReportingPeriodQueryString()}`)
    this.setState({ reportingPeriod: value })
  }

  periodCountChange = (value) => {
    value = sanitizePeriodCount(value)
    if (value === getPeriodCount()) {
      return
    }

    invalidateGameData(getReportingPeriod())
    setPeriodCount(getReportingPeriod(), value)
    this.props.history.replace(`${window.location.pathname}?${getReportingPeriodQueryString()}`)
    this.setState({ periodCount: value })
  }

  endDateChange = (value) => {
    value = sanitizeEndDate(new Date(value))
    if (value === getEndDate()) {
      return
    }
    invalidateGameData(getReportingPeriod())
    setEndDate(value)
    this.props.history.replace(`${window.location.pathname}?${getReportingPeriodQueryString()}`)
    this.setState({ endDate: value })
  }

  componentDidMount() {
    const propsReportingPeriod = retreiveReportingPeriodFromQueryString()
    const propsPeriodCount = retrievePeriodCountFromQueryString()
    const propsEndDate = retrieveEndDateFromQueryString()

    setReportingPeriod(propsReportingPeriod)
    setPeriodCount(propsReportingPeriod, propsPeriodCount)
    setEndDate(propsEndDate)

    this.props.history.replace(`${window.location.pathname}?${getReportingPeriodQueryString()}`)

    this.setState({ reportingPeriod: propsReportingPeriod, periodCount: propsPeriodCount, endDate: propsEndDate })
  }

  componentDidUpdate() {
    setTimeout(() => {
      const propsReportingPeriod = retreiveReportingPeriodFromQueryString()
      const propsPeriodCount = retrievePeriodCountFromQueryString()
      const propsEndDate = retrieveEndDateFromQueryString()

      if (
        propsReportingPeriod !== this.state.reportingPeriod ||
        propsPeriodCount !== this.state.periodCount ||
        propsEndDate !== this.state.endDate
      ) {
        invalidateGameData(propsReportingPeriod)
        setReportingPeriod(propsReportingPeriod)
        setPeriodCount(propsReportingPeriod, propsPeriodCount)
        setEndDate(propsEndDate)

        this.setState({ reportingPeriod: propsReportingPeriod, periodCount: propsPeriodCount, endDate: propsEndDate })
      }
    }, 1)
  }

  render() {
    return (
      <SCThemeProvider theme={{ ...scStyles.defaultTheme, colors: scColors }}>
        <ThemeProvider theme={theme}>
          <LegacyThemeProvider theme={theme}>
            <TopNavigation
              reportingPeriod={this.state.reportingPeriod}
              endDate={this.state.endDate}
              periodCount={this.state.periodCount}
              onScopeChange={this.scopeChange}
              onPeriodChange={this.periodChange}
              onPeriodCountChange={this.periodCountChange}
              onEndDateChange={this.endDateChange}
              allowCustomRange={allowCustomDateRange()}
            />
            <Page>
              <RouteContent />
            </Page>
            <Footer />
            <ModalContainer />
          </LegacyThemeProvider>
        </ThemeProvider>
      </SCThemeProvider>
    )
  }
}

class EmbedApp extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      reportingPeriod: getReportingPeriod()
    }
  }

  periodChange = (value) => {
    if (value === getReportingPeriod()) {
      return
    }

    setReportingPeriod(value)
  }

  componentDidMount() {
    document.body.className = 'embed'
    const propsReportingPeriod = retreiveReportingPeriodFromQueryString()
    const propsPeriodCount = retrievePeriodCountFromQueryString()
    const propsEndDate = retrieveEndDateFromQueryString()

    if (propsReportingPeriod !== getReportingPeriod()) {
      this.periodChange(propsReportingPeriod)
    }

    setPeriodCount(propsReportingPeriod, propsPeriodCount)
    setEndDate(propsEndDate)
  }

  render() {
    return (
      <SCThemeProvider theme={{ ...scStyles.defaultTheme, colors: scColors }}>
        <ThemeProvider theme={theme}>
          <LegacyThemeProvider theme={theme}>
            <RouteContent />
          </LegacyThemeProvider>
        </ThemeProvider>
      </SCThemeProvider>
    )
  }
}

const container = document.getElementById('root')
const root = createRoot(container)
root.render(<App />)
